import {
  Hand,
  Maximize,
  MessageCircle,
  Minus,
  MousePointer2,
  PackagePlus,
  Paperclip,
  Pencil,
  Plus,
  Stamp,
  Undo2,
  ZoomIn,
} from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import { Tooltip } from 'src/shared/components/ui/tooltip'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOptionalOrderCatalogControl } from '../../OrderCatalogWidget/OrderCatalogControlContext'
import type { CanvasTool } from '../types'

const ToolbarTooltip = ({
  content,
  side = 'right',
  children,
}: {
  content: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  children: React.ReactNode
}) => (
  <Tooltip content={content} delayDuration={0} side={side}>
    {children}
  </Tooltip>
)

export const CanvasToolbar = ({
  activeTool,
  onToolChange,
  onAttachFile,
  canUndoStroke,
  onUndoStroke,
  scale,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onFit,
  zoomOnly = false,
}: {
  activeTool: CanvasTool
  onToolChange: (tool: CanvasTool) => void
  onAttachFile: () => void
  canUndoStroke: boolean
  onUndoStroke: () => void
  scale: number
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  onFit: () => void
  zoomOnly?: boolean
}) => {
  const { t } = useLocalization()
  const catalog = useOptionalOrderCatalogControl()

  const tools: {
    id: CanvasTool
    icon: React.ReactNode
    label: string
    shortcut: string
  }[] = [
    {
      id: 'pan',
      icon: <Hand className="h-3.5 w-3.5" />,
      label: t('Hand', 'Hand'),
      shortcut: 'H',
    },
    {
      id: 'select',
      icon: <MousePointer2 className="h-3.5 w-3.5" />,
      label: t('Select', 'Välj'),
      shortcut: 'V',
    },
    {
      id: 'comment',
      icon: <MessageCircle className="h-3.5 w-3.5" />,
      label: t('Comment', 'Kommentera'),
      shortcut: 'C',
    },
    {
      id: 'draw',
      icon: <Pencil className="h-3.5 w-3.5" />,
      label: t('Draw', 'Rita'),
      shortcut: 'P',
    },
    {
      id: 'stamp',
      icon: <Stamp className="h-3.5 w-3.5" />,
      label: t('Stamp', 'Stämpel'),
      shortcut: 'E',
    },
  ]

  const zoomButtons = (
    <>
      <ToolbarTooltip side={zoomOnly ? 'right' : 'top'} content={t('Zoom out', 'Zooma ut')}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onZoomOut}
          aria-label={t('Zoom out', 'Zooma ut')}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
      </ToolbarTooltip>
      <ToolbarTooltip
        side={zoomOnly ? 'right' : 'top'}
        content={t('Reset zoom', 'Återställ zoom')}
      >
        <button
          type="button"
          className="min-w-11 text-center text-xs tabular-nums text-gray-700"
          onClick={onResetZoom}
          aria-label={t('Reset zoom', 'Återställ zoom')}
        >
          {Math.round(scale * 100)}%
        </button>
      </ToolbarTooltip>
      <ToolbarTooltip side={zoomOnly ? 'right' : 'top'} content={t('Zoom in', 'Zooma in')}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onZoomIn}
          aria-label={t('Zoom in', 'Zooma in')}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </ToolbarTooltip>
      <ToolbarTooltip
        side={zoomOnly ? 'right' : 'top'}
        content={t('Fit to content', 'Anpassa till innehåll')}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={onFit}
          aria-label={t('Fit to content', 'Anpassa till innehåll')}
        >
          <Maximize className="h-3.5 w-3.5" />
        </Button>
      </ToolbarTooltip>
    </>
  )

  if (zoomOnly) {
    return (
      <div
        className="absolute left-4 top-1/2 z-40 flex -translate-y-1/2 flex-col items-center gap-0.5 rounded-2xl border border-gray-200 bg-white/90 p-1 shadow-lg backdrop-blur max-sm:bottom-[max(1rem,env(safe-area-inset-bottom))] max-sm:left-1/2 max-sm:top-auto max-sm:flex-row max-sm:-translate-x-1/2 max-sm:translate-y-0"
        onPointerDown={(e) => e.stopPropagation()}
      >
        {zoomButtons}
      </div>
    )
  }

  return (
    <Column
      className="absolute left-4 top-1/2 z-40 -translate-y-1/2 items-center gap-0.5 rounded-2xl border border-gray-200 bg-white/90 p-1 shadow-lg backdrop-blur max-sm:bottom-[5.5rem] max-sm:top-auto max-sm:translate-y-0"
      onPointerDown={(e) => e.stopPropagation()}
    >
      {catalog ? (
        <>
          <ToolbarTooltip
            content={t(
              'Add products to this order',
              'Lägg till produkter i ordern'
            )}
          >
            <Button
              variant="primary"
              size="icon"
              onClick={catalog.openCatalog}
              aria-label={t('Add products', 'Lägg till produkter')}
              className={cn(!catalog.hasItems && 'heartbeat-border')}
            >
              <PackagePlus className="h-3.5 w-3.5" />
            </Button>
          </ToolbarTooltip>
          <div className="my-0.5 h-px w-5 bg-gray-200" />
        </>
      ) : null}

      {tools.map((tool) => (
        <ToolbarTooltip
          key={tool.id}
          content={`${tool.label} (${tool.shortcut})`}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToolChange(tool.id)}
            aria-label={tool.label}
            aria-pressed={activeTool === tool.id}
            aria-keyshortcuts={tool.shortcut}
            className={cn(
              activeTool === tool.id &&
                'bg-gray-200 text-foreground hover:bg-gray-200 hover:text-foreground'
            )}
          >
            {tool.icon}
          </Button>
        </ToolbarTooltip>
      ))}

      <div className="my-0.5 h-px w-5 bg-gray-200" />

      <ToolbarTooltip content={`${t('Attach file', 'Bifoga fil')} (F)`}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onAttachFile}
          aria-label={t('Attach file', 'Bifoga fil')}
          aria-keyshortcuts="F"
        >
          <Paperclip className="h-3.5 w-3.5" />
        </Button>
      </ToolbarTooltip>

      <Popover>
        <ToolbarTooltip content={t('Zoom', 'Zoom')}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('Zoom', 'Zoom')}
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
        </ToolbarTooltip>
        <PopoverContent
          side="right"
          align="center"
          sideOffset={8}
          className="w-auto rounded-full border-gray-200 bg-white/90 p-1 shadow-lg backdrop-blur"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Row className="items-center gap-0.5">
            {zoomButtons}
          </Row>
        </PopoverContent>
      </Popover>

      {activeTool === 'draw' && canUndoStroke && (
        <ToolbarTooltip content={t('Undo stroke', 'Ångra linje')}>
          <Button
            variant="ghost"
            size="icon"
            onClick={onUndoStroke}
            aria-label={t('Undo stroke', 'Ångra linje')}
          >
            <Undo2 className="h-3.5 w-3.5" />
          </Button>
        </ToolbarTooltip>
      )}
    </Column>
  )
}
